import AccountTreeRounded from "@mui/icons-material/AccountTreeRounded";
import BrowseGalleryRounded from "@mui/icons-material/BrowseGalleryRounded";
import CrisisAlertRounded from "@mui/icons-material/CrisisAlertRounded";
import MiscellaneousServicesRounded from "@mui/icons-material/MiscellaneousServicesRounded";
import PushPinRounded from "@mui/icons-material/PushPinRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { ReactElement } from "react";
import pageData from "../../data/pages/solutions-intelligent-alerting.json";
import { CardsSection } from "../../components/CardsSection";
import { MdaiTable } from "../../components/MdaiTable";
import { PageTemplate } from "../../components/PageTemplate";
import { TextGroup } from "../../components/TextGroup";
import type { SolutionDetailCard } from "../../types/solutionDetailPageTemplate";
import type { SolutionDetailPageTemplate } from "../../types/solutionDetailPageTemplate";
import { resolveCdnAssetUrl } from "../../utils/cdnAssetUrl";
import { mapCardsWithIcons } from "./utils/mapCardsWithIcons";

interface IntelligentAlertingPageTemplate extends SolutionDetailPageTemplate {
  capabilityCards: SolutionDetailCard[];
  valueCards: SolutionDetailCard[];
}

const typedPageData = pageData as IntelligentAlertingPageTemplate;
const CAPABILITY_ICONS = [
  BrowseGalleryRounded,
  TuneRounded,
  CrisisAlertRounded,
] as const;
const VALUE_ICONS = [PushPinRounded, AccountTreeRounded, MiscellaneousServicesRounded] as const;

const IntelligentAlertsCapabilityCards = mapCardsWithIcons({
  cards: typedPageData.capabilityCards,
  icons: CAPABILITY_ICONS,
  fallbackIcon: BrowseGalleryRounded,
});

const IntelligentAlertsValueCards = mapCardsWithIcons({
  cards: typedPageData.valueCards,
  icons: VALUE_ICONS,
  fallbackIcon: PushPinRounded,
});

const capabilitiesSection = typedPageData.cardsSections?.find((s) => s.id === "capabilities");
const valueSection = typedPageData.cardsSections?.find((s) => s.id === "value");

export default function IntelligentAlerting(): ReactElement {
  return (
    <Box>
      <PageTemplate
        id="intelligent-alerting"
        hero={{
          title: typedPageData.hero.title,
          subtitle: typedPageData.hero.subtitle,
          image: typedPageData.hero.image
            ? {
                src: resolveCdnAssetUrl(typedPageData.hero.image.src),
                alt: typedPageData.hero.image.alt,
                flip: typedPageData.hero.image.flip,
              }
            : undefined,
          buttons: typedPageData.hero.button
            ? [{ text: typedPageData.hero.button.text, href: typedPageData.hero.button.href }]
            : [],
        }}
        topTabs={{
          headline: typedPageData.topTabs.headline,
          caption: (
            <Typography variant="subtitle2" mb={4}>
              {typedPageData.topTabs.caption}
            </Typography>
          ),
          tabs: (typedPageData.topTabs.tabs ?? []).map((tab) => ({
            src: resolveCdnAssetUrl(tab.src),
            title: tab.title,
            type: tab.type ?? "image",
            alt: tab.alt,
          })),
        }}
        sections={
          <>
            <CardsSection
              title={capabilitiesSection?.title}
              cards={IntelligentAlertsCapabilityCards}
              cardsPerRow={{ lg: 3 }}
              closingText={
                capabilitiesSection?.closingText ? (
                  <Typography variant="body1" mt={3}>
                    {capabilitiesSection.closingText}
                  </Typography>
                ) : undefined
              }
            />

            {typedPageData.textGroups?.[0] && (
              <TextGroup
                id="intelligent-alerting-tg-1"
                title={typedPageData.textGroups[0].title}
                subtitle={typedPageData.textGroups[0].subtitle}
                bodies={typedPageData.textGroups[0].bodies}
                bullets={typedPageData.textGroups[0].bullets}
              />
            )}

            {typedPageData.textGroups?.[1] && (
              <TextGroup
                id="intelligent-alerting-tg-2"
                title={typedPageData.textGroups[1].title}
                subtitle={typedPageData.textGroups[1].subtitle}
                bodies={typedPageData.textGroups[1].bodies}
                bullets={typedPageData.textGroups[1].bullets}
              />
            )}

            {typedPageData.table && (
              <MdaiTable
                id="intelligent-alerting"
                tableTitle={typedPageData.table.title}
                headers={typedPageData.table.headers}
                rows={typedPageData.table.rows}
              />
            )}
            <CardsSection
              title={valueSection?.title}
              description={
                valueSection?.description ? (
                  <Typography variant="body1">
                    {valueSection.description}
                  </Typography>
                ) : undefined
              }
              closingText={
                valueSection?.closingText ? (
                  <Typography variant="body1" mt={3}>
                    {valueSection.closingText}
                  </Typography>
                ) : undefined
              }
              cards={IntelligentAlertsValueCards}
              cardsPerRow={{ lg: 3 }}
            />
          </>
        }
        bottomButton={typedPageData.bottomButton}
      />
    </Box>
  );
}
